import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  UtensilsCrossed, LogIn, ArrowLeft, ArrowRight, ShieldCheck, Briefcase,
  Calculator, ConciergeBell, ChefHat, User, ShieldAlert, RefreshCw, KeyRound, Bike,
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { PinPad } from '../components/common/PinPad';
import { Logo } from '../components/common/Logo';
import { BootSplash } from '../components/boot/BootSplash';
import Landing from './Landing';

const ROLE_ICON = {
  admin: ShieldCheck, manager: Briefcase, cashier: Calculator,
  waiter: ConciergeBell, kitchen: ChefHat, corriere: Bike,
};

// Footer with the legal copyright line. The tiny logo at the end is an almost
// imperceptible shortcut to the Gestore (super-admin) portal.
const HiddenGestoreFooter = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="mt-6 pt-4 border-t border-border flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground select-none">
      <span>© {new Date().getFullYear()} {t('common.appName')} · {t('login.rightsReserved')}</span>
      <button
        type="button"
        onClick={() => navigate('/gestore/login')}
        data-testid="gestore-link"
        aria-label="."
        title=""
        className="opacity-20 hover:opacity-100 focus-visible:opacity-100 transition-opacity duration-200 outline-none"
      >
        <UtensilsCrossed className="h-3 w-3" />
      </button>
    </div>
  );
};

const homeForRole = (role) => {
  if (role === 'waiter') return '/waiter';
  if (role === 'kitchen') return '/cucina-display';
  if (role === 'corriere') return '/corriere';
  return '/tavoli';
};

const Brand = () => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-2 mb-6">
      <div className="flex items-center gap-2">
        <Logo className="h-11 w-11" />
        <div>
          <div className="font-display font-bold leading-tight">{t('common.appName')}</div>
          <div className="text-xs text-muted-foreground">{t('login.subtitle')}</div>
        </div>
      </div>
      <LanguageSwitcher variant="compact" onChange={(code) => localStorage.setItem('rmp_login_lang', code)} />
    </div>
  );
};

export default function AccessFlow() {
  const { getDeviceState, unlockDevice, resetDevice, getProfiles, sessionLogin } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState('boot'); // boot | landing | unlock | profiles | license
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // unlock form
  const [email, setEmail] = useState(() => localStorage.getItem('vexel_device_email') || '');
  const [adminPw, setAdminPw] = useState('');

  // profiles
  const [profiles, setProfiles] = useState([]);
  const [selected, setSelected] = useState(null);
  const [courierOpen, setCourierOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [profilePw, setProfilePw] = useState('');
  const [adminUsePw, setAdminUsePw] = useState(false);

  const stateRef = useRef(null);

  // ---- Boot: run the 3s splash and resolve device state in parallel ----
  useEffect(() => {
    let splashDone = false;
    let stateDone = false;
    const proceed = () => {
      if (!(splashDone && stateDone)) return;
      const s = stateRef.current;
      if (s === 'license_blocked') setStep('license');
      else if (s === 'ok') setStep('profiles');
      else setStep('landing'); // dispositivo non ancora attivato -> mostra la landing
    };
    const timer = setTimeout(() => { splashDone = true; proceed(); }, 3000);
    getDeviceState()
      .then((d) => { stateRef.current = d.status; if (d.restaurant) setRestaurant(d.restaurant); })
      .catch(() => { stateRef.current = 'first_login'; })
      .finally(() => { stateDone = true; proceed(); });
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Load profiles when entering that step ----
  useEffect(() => {
    if (step !== 'profiles') return;
    setSelected(null); setPin(''); setProfilePw(''); setError('');
    getProfiles().then(setProfiles).catch(() => setError(t('access.error')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const finishLogin = (user) => {
    toast.success(t('login.welcome', { name: user.name }));
    navigate(homeForRole(user.role), { replace: true });
  };

  const submitUnlock = async (e) => {
    e.preventDefault();
    if (!email.trim() || !adminPw) return;
    setError(''); setLoading(true);
    try {
      const res = await unlockDevice({ email: email.trim(), password: adminPw });
      setRestaurant(res.restaurant);
      setAdminPw('');
      setStep('profiles');
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) { setError(detail || t('access.deviceInUse')); }
      else if (status === 403) { setError(detail || t('access.licenseHint')); }
      else setError(detail || t('access.invalidAdmin'));
    } finally { setLoading(false); }
  };

  const doSessionLogin = async (credential, kind) => {
    if (!selected) return;
    const useKind = kind || (selected.role === 'admin' ? 'password' : 'pin');
    setError(''); setLoading(true);
    try {
      const user = await sessionLogin({
        profileId: selected.id,
        password: useKind === 'password' ? credential : undefined,
        pin: useKind === 'pin' ? credential : undefined,
      });
      finishLogin(user);
    } catch (err) {
      setError(err?.response?.data?.detail || t('login.invalidCredentials'));
      setPin('');
    } finally { setLoading(false); }
  };

  const recheck = async () => {
    setLoading(true);
    try {
      const d = await getDeviceState();
      stateRef.current = d.status;
      if (d.restaurant) setRestaurant(d.restaurant);
      if (d.status === 'license_blocked') setStep('license');
      else if (d.status === 'ok') setStep('profiles');
      else setStep('unlock');
    } finally { setLoading(false); }
  };

  if (step === 'boot') return <BootSplash durationMs={3000} />;

  // ---- Landing di presentazione (solo prima del primo accesso/attivazione) ----
  if (step === 'landing') return <Landing onEnter={() => setStep('unlock')} />;

  // ---- License blocked ----
  if (step === 'license') {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md p-8 text-center" data-testid="license-blocked">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-destructive/10">
            <ShieldAlert className="h-7 w-7 text-destructive" />
          </div>
          <h1 className="font-display text-xl font-extrabold">{t('access.licenseTitle')}</h1>
          {restaurant?.name && <p className="mt-1 text-sm font-medium">{restaurant.name}</p>}
          <p className="mt-2 text-sm text-muted-foreground">{t('access.licenseHint')}</p>
          <Button onClick={recheck} disabled={loading} variant="outline" className="mt-5 rounded-xl" data-testid="license-retry-button">
            <RefreshCw className="h-4 w-4 mr-2" /> {t('access.retry')}
          </Button>
          <HiddenGestoreFooter />
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md p-8">
        <Brand />

        {/* ---- First login: device unlock (admin) ---- */}
        {step === 'unlock' && (
          <form onSubmit={submitUnlock} className="space-y-4" data-testid="device-unlock">
            <div className="flex items-center gap-2 rounded-xl bg-primary/10 text-primary px-3 py-2.5">
              <KeyRound className="h-4 w-4 shrink-0" />
              <div className="text-sm">
                <div className="font-semibold">{t('access.firstLoginTitle')}</div>
                <div className="text-xs text-primary/80">{t('access.firstLoginHint')}</div>
              </div>
            </div>
            <div>
              <Label htmlFor="admin-email">{t('access.adminEmail')}</Label>
              <Input id="admin-email" data-testid="device-email-input" value={email}
                onChange={(e) => { setEmail(e.target.value); localStorage.setItem('vexel_device_email', e.target.value); }} placeholder={t('login.identifierPlaceholder')}
                className="mt-1.5 h-12" autoCapitalize="none" autoComplete="username" />
            </div>
            <div>
              <Label htmlFor="admin-pw">{t('login.password')}</Label>
              <Input id="admin-pw" type="password" data-testid="device-password-input" value={adminPw}
                onChange={(e) => setAdminPw(e.target.value)} placeholder="••••••••" className="mt-1.5 h-12" />
            </div>
            {error && <div className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2" data-testid="access-error">{error}</div>}
            <Button type="submit" data-testid="device-unlock-button" disabled={loading} className="w-full h-12 text-base rounded-xl">
              <KeyRound className="h-5 w-5 mr-2" /> {loading ? t('login.accessing') : t('access.unlock')}
            </Button>
          </form>
        )}

        {/* ---- Second login: profile selection (always) ---- */}
        {step === 'profiles' && !selected && (
          <div className="space-y-4" data-testid="profile-selector">
            <div>
              <div className="font-display text-xl font-extrabold">{t('access.selectProfile')}</div>
              <p className="text-sm text-muted-foreground mt-0.5">{t('access.selectProfileHint')}</p>
            </div>
            {error && <div className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2" data-testid="access-error">{error}</div>}
            {(() => {
              const couriers = profiles.filter((p) => p.role === 'corriere');
              const others = profiles.filter((p) => p.role !== 'corriere');
              if (courierOpen) {
                return (
                  <div className="space-y-2" data-testid="courier-group-list">
                    <button type="button" onClick={() => setCourierOpen(false)} data-testid="courier-group-back"
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                      <ArrowLeft className="h-4 w-4" /> {t('login.back')}
                    </button>
                    <div className="grid grid-cols-1 gap-2">
                      {couriers.map((p) => (
                        <button key={p.id} type="button" data-testid={`profile-option-${p.username}`}
                          onClick={() => { setSelected(p); setError(''); setPin(''); setProfilePw(''); }}
                          className="flex items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition-colors hover:bg-muted hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                          <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0"><Bike className="h-5 w-5" /></div>
                          <div className="min-w-0 flex-1">
                            <div className="font-display font-bold text-sm leading-tight truncate">{p.name}</div>
                            <div className="text-xs text-muted-foreground">{t('roles.corriere', { defaultValue: 'Corriere' })}</div>
                          </div>
                          <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }
              return (
                <div className="grid grid-cols-1 gap-2">
                  {others.map((p) => {
                    const Icon = ROLE_ICON[p.role] || User;
                    return (
                      <button key={p.id} type="button" data-testid={`profile-option-${p.username}`}
                        onClick={() => { setSelected(p); setError(''); setPin(''); setProfilePw(''); }}
                        className="flex items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition-colors hover:bg-muted hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                        <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0"><Icon className="h-5 w-5" /></div>
                        <div className="min-w-0 flex-1">
                          <div className="font-display font-bold text-sm leading-tight truncate">{p.name}</div>
                          <div className="text-xs text-muted-foreground">{t(`roles.${p.role}`, { defaultValue: p.role })}</div>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                      </button>
                    );
                  })}
                  {couriers.length > 0 && (
                    <button type="button" data-testid="profile-group-corriere" onClick={() => setCourierOpen(true)}
                      className="flex items-center gap-3 rounded-xl border border-border bg-card p-3 text-left transition-colors hover:bg-muted hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                      <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0"><Bike className="h-5 w-5" /></div>
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-bold text-sm leading-tight truncate">{t('roles.corriere', { defaultValue: 'Corriere' })}</div>
                        <div className="text-xs text-muted-foreground">{couriers.length} {couriers.length === 1 ? t('roles.corriere', { defaultValue: 'Corriere' }) : t('nav.delivery')}</div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </button>
                  )}
                </div>
              );
            })()}
            <div className="pt-2 text-center">
              <button type="button" onClick={() => { resetDevice(); setStep('unlock'); }} data-testid="reset-device-link"
                className="text-xs text-muted-foreground hover:text-primary underline underline-offset-2">
                {t('access.otherAdmin')}
              </button>
            </div>
          </div>
        )}

        {/* ---- Credential entry for the selected profile ---- */}
        {step === 'profiles' && selected && (
          <div className="space-y-4" data-testid="profile-credential">
            <button type="button" onClick={() => { setSelected(null); setError(''); }} data-testid="profile-back-button"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-4 w-4" /> {t('login.back')}
            </button>
            <div className="flex items-center gap-2.5 rounded-xl border border-border bg-muted/40 p-2.5">
              {(() => { const Icon = ROLE_ICON[selected.role] || User; return (
                <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><Icon className="h-5 w-5" /></div>
              ); })()}
              <div className="min-w-0">
                <div className="font-display font-bold text-sm leading-tight truncate">{selected.name}</div>
                <div className="text-xs text-muted-foreground">{t(`roles.${selected.role}`, { defaultValue: selected.role })}</div>
              </div>
            </div>

            {(selected.role === 'admin' && adminUsePw) ? (
              <form onSubmit={(e) => { e.preventDefault(); if (profilePw) doSessionLogin(profilePw, 'password'); }} className="space-y-4">
                <div>
                  <Label htmlFor="profile-pw">{t('access.enterPassword')}</Label>
                  <Input id="profile-pw" type="password" data-testid="profile-password-input" value={profilePw}
                    onChange={(e) => setProfilePw(e.target.value)} placeholder="••••••••" className="mt-1.5 h-12" autoFocus />
                </div>
                {error && <div className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2 text-center" data-testid="access-error">{error}</div>}
                <Button type="submit" data-testid="profile-login-button" disabled={loading} className="w-full h-12 text-base rounded-xl">
                  <LogIn className="h-5 w-5 mr-2" /> {loading ? t('login.accessing') : t('login.login')}
                </Button>
                <button type="button" onClick={() => { setAdminUsePw(false); setError(''); setProfilePw(''); }} data-testid="admin-use-pin"
                  className="w-full text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
                  {t('access.usePin', { defaultValue: 'Usa PIN' })}
                </button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="text-center text-sm font-medium text-muted-foreground">{t('access.enterPin')}</div>
                <PinPad value={pin} onChange={setPin} length={6} onComplete={(v) => doSessionLogin(v, 'pin')} disabled={loading} />
                {error && <div className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2 text-center" data-testid="access-error">{error}</div>}
                <Button type="button" data-testid="profile-login-button" disabled={loading || pin.length < 6}
                  onClick={() => doSessionLogin(pin, 'pin')} className="w-full h-12 text-base rounded-xl">
                  <LogIn className="h-5 w-5 mr-2" /> {loading ? t('login.accessing') : t('login.login')}
                </Button>
                {selected.role === 'admin' && (
                  <button type="button" onClick={() => { setAdminUsePw(true); setError(''); setPin(''); }} data-testid="admin-use-password"
                    className="w-full text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
                    {t('access.usePassword', { defaultValue: 'Usa password' })}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        <HiddenGestoreFooter />
      </Card>
    </div>
  );
}
